// src/pages/PropertyContacts/components/PropertyContactDetailModal.tsx
import React from "react";
import { X, Phone, Calendar, Clock } from "lucide-react";
import { Modal } from "../../../components/ui";
import { formatDate } from "../../../utils/formatters";
import type { PropertyContactResponseDto } from "../../../types/property-contact";

interface PropertyContactDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: PropertyContactResponseDto | null;
}

const PropertyContactDetailModal: React.FC<PropertyContactDetailModalProps> = ({
  isOpen,
  onClose,
  contact,
}) => {
  if (!contact) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="bg-blue-100 rounded-lg p-2 mr-3">
              <Phone className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Property Contact Details
              </h3>
              <p className="text-sm text-gray-500">Contact ID: #{contact.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Contact Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Contact Information
            </h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <Phone className="h-4 w-4 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="font-medium text-gray-900">{contact.phone}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Timestamps
            </h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(contact.createdAt)}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <Clock className="h-4 w-4 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Last Updated</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(contact.updatedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Actions */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">
              Quick Actions
            </h4>
            <div className="flex space-x-3">
              <a
                href={`tel:${contact.phone}`}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg text-center text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                Call Now
              </a>
              <a
                href={`sms:${contact.phone}`}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg text-center text-sm font-medium hover:bg-green-700 transition-colors"
              >
                Send SMS
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PropertyContactDetailModal;